import DatePickerNative from '@/components/UI/DatePickerNative';
import FormRender from '@/components/UI/FormRender';
import { _isNotBlank } from '@/littledash';
import type { Request, RequestTemplate } from '@/model/Request.model';
import { DateUtils } from '@/utils/Date.utils';
import { cloneElement, type FC, type PropsWithChildren, type ReactElement } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

interface RequestDetailsProps {
  ActionButtons: ReactElement;
  onSubmit: (request: Request) => Promise<void>;
  requestTemplate: RequestTemplate;
  request: Request;
  setRequest: React.Dispatch<React.SetStateAction<Request>>;
}

type FormDataEntries = Record<string, string>;

interface RequestDetailsFormData {
  form_data: FormDataEntries;
  due_date: string;
}

const buildDefaultFormData = (template: RequestTemplate): FormDataEntries =>
  template.form.groups.reduce<FormDataEntries>((acc, group) => {
    group.fields.forEach((field) => {
      acc[field.name] = '';
    });
    return acc;
  }, {});

const RequestDetails: FC<RequestDetailsProps> = ({ ActionButtons, onSubmit, requestTemplate, request, setRequest }) => {
  const formMethods = useForm<RequestDetailsFormData>({
    defaultValues: {
      form_data: { ...buildDefaultFormData(requestTemplate), ...(request?.form_data ?? {}) },
      due_date: DateUtils.isValidDate(request?.due_date) ? (request.due_date ?? '') : '',
    },
  });

  const handleSubmit = (submitData: RequestDetailsFormData) => {
    const { due_date: dueDate, form_data: formData } = submitData;
    const updateRequestCallback = (prevRequest: Request): Request => {
      const updatedRequest: Request = {
        ...prevRequest,
        form_data: formData,
      };
      // Mirror the picker state: clearing the input must clear the saved due_date,
      // otherwise the previously stored value would silently survive submit.
      if (_isNotBlank(dueDate)) {
        updatedRequest.due_date = dueDate;
      } else {
        delete updatedRequest.due_date;
      }
      return updatedRequest;
    };
    setRequest(updateRequestCallback);
    onSubmit(updateRequestCallback(request));
  };

  const groupsWithNames = requestTemplate.form.groups.map((group) => ({
    ...group,
    fields: Array.isArray(group.fields)
      ? group.fields.map((field) => ({
          ...field,
          name: `form_data.${field.name}`,
        }))
      : [],
  }));

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={formMethods.handleSubmit(handleSubmit)}
        className="ui-card pa3"
        data-test-component="RequestDetails"
        data-test-element="container"
      >
        <FormRender groupContainer={GroupContainer} groups={groupsWithNames} />
        <GroupContainer testId="due-date-container">
          <label className="mb2" htmlFor="due-date">
            Due Date (Optional)
          </label>
          <Controller
            name="due_date"
            control={formMethods.control}
            render={({ field: { value, onChange } }) => (
              <DatePickerNative id="due-date" value={value} style={{ marginBottom: 0 }} onChange={onChange} />
            )}
          />
        </GroupContainer>
        {cloneElement(ActionButtons, {
          onOk: formMethods.handleSubmit(handleSubmit),
        })}
      </form>
    </FormProvider>
  );
};

export default RequestDetails;

const GroupContainer: FC<PropsWithChildren<{ testId?: string }>> = ({ children, testId }) => (
  <div className="mb4 mw7" data-test-component="GroupContainer" data-test-element="container" data-testid={testId}>
    {children}
  </div>
);
